import type { BaseAPIResponse } from '~/types/api';
import type { Contact } from '~/types/person/contacts';
import { ApiError } from '~/utils/errors';

export type Activity = {
    id: string;
    type: string;
    from_type: string;
    from_id: number;
    from: string;
    to_type: string;
    to_id: number;
    to: string;
    message: any | null;
    created_at: string;
};

export const contactsService = {
    async getContacts(personId: number): Promise<Contact[]> {
        const nuxtApp = useNuxtApp();
        
        const { data, status, message } = await usePbApi<BaseAPIResponse<Contact[]>>(
            nuxtApp.$api.routes.people.contacts.get_contacts(personId),
            {
                method: 'GET',
            }
        );
        
        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch contacts', status);
        }
        
        if (!data) {
            throw new ApiError('No contacts data received', 404);
        }
        
        return (data as BaseAPIResponse<Contact[]>).data;
    },

    async getActivities(limit: number = 10000): Promise<Activity[]> {
        const nuxtApp = useNuxtApp();
        
        const { data, status, message } = await usePbApi<Activity[]>(
            nuxtApp.$api.routes.apps.buzz.get_activity(limit)
        );
        
        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch activities', status);
        }
        
        if (!data) {
            throw new ApiError('No activities data received', 404);
        }
        
        return data;
    },

    async createContact(personId: number, contactData: any): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.contacts.create_contact(personId),
                {
                    method: 'POST',
                    body: [contactData],
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to create contact', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateContact(personId: number, contactId: number, contactData: any): Promise<Contact> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<Contact>>(
                nuxtApp.$api.routes.people.contacts.update_contact(personId, contactId),
                {
                    method: 'PATCH',
                    body: contactData,
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to update contact', status);
            }

            if (!data) {
                throw new ApiError('No contact data received', 404);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async sendMessage(message: object): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.apps.buzz.log_activity(),
                {
                    method: 'POST',
                    body: message,
                }
            );
        } catch (error: any) {
            throw new Error(error.data?.message || error.message || 'Failed to send message');
        }
    },

    async deleteContact(personId: number, contactId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.contacts.delete(personId, contactId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete contact', status);
            }
        } catch (error: any) {
            throw new Error(error.data?.message || error.message || 'Failed to delete contact');
        }
    }
}; 